import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressPassword from '../../../components/ProgressPassword';
import { ControlpanelPrimaryButton, ControlpanelSecondaryButton, GrayIconButton } from '../../../components/StyledButton';
import { CustomDialog } from '../../../components/StyledComponents';
import CustomInput from './../../../components/CustomInput';

const ExportDataDialog = ({ onClose }) => {
   const { t } = useTranslation();
   const [inputChecked, setInputChecked] = useState(true);
   const [selectedExportOptions, setSelectedExportOptions] = useState('downloadFile');
   const [formData, setFormData] = useState({
      password: '',
      ftpHost: '',
      filePath: '',
      ftpUserName: '',
      ftpPassword: '',
   });

   const handleInputChange = (e) => {
      const { name, value } = e.target;
      setFormData((prevData) => ({
         ...prevData,
         [name]: value,
      }));
   };

   const handleChangeExportOptions = (event) => {
      setSelectedExportOptions(event.target.value);
   };

   return (
      <CustomDialog transitionDuration={300} maxWidth={'xl'} open onClose={onClose} keepMounted>
         <DialogTitle>
            <div className='flex justify-between'>
               <div className='flex items-center gap-4'>
                  <p className='text-xl font-semibold '>{t('button.export-data')}</p>
               </div>
               <GrayIconButton onClick={onClose}>
                  <CloseIcon />
               </GrayIconButton>
            </div>
         </DialogTitle>

         <DialogContent>
            <div className='export-data-options pt-6'>
               <div className='export-data-options-container'>
                  <div className='grid grid-cols-10 pt-5'>
                     <p className='col-span-10 md:col-span-3 flex items-center text-sm'>{t('common.export-options')}</p>
                     <div className='col-span-10 md:col-span-7'>
                        <FormControl component='fieldset'>
                           <RadioGroup
                              name='exportOption'
                              value={selectedExportOptions}
                              onChange={handleChangeExportOptions}
                              className='flex !flex-row'
                           >
                              <FormControlLabel value='downloadFile' control={<Radio />} label={t('common.download-file')} />
                              <FormControlLabel value='FTPServer' control={<Radio />} label={t('common.save-to-ftp-server-location')} />
                           </RadioGroup>
                        </FormControl>
                     </div>
                     {selectedExportOptions === 'FTPServer' && (
                        <div className='col-span-10 bg-[#e3e6f0] p-3 rounded'>
                           <div className='grid grid-cols-2 gap-x-5'>
                              <div className='col-span-1'>
                                 <CustomInput
                                    required={false}
                                    title={t('common.ftp-host')}
                                    inputType='text'
                                    name='ftpHost'
                                    handleInputChange={handleInputChange}
                                    value={formData.ftpHost}
                                 />
                              </div>
                              <div className='col-span-1'>
                                 <CustomInput
                                    required={false}
                                    title={t('common.file-path')}
                                    inputType='text'
                                    name='filePath'
                                    handleInputChange={handleInputChange}
                                    value={formData.filePath}
                                 />
                              </div>
                           </div>
                           <div className='flex items-center pt-2'>
                              <Checkbox
                                 className='!p-0'
                                 checked={inputChecked}
                                 onChange={(e) => {
                                    setInputChecked(e.target.checked);
                                    if (!e.target.checked) {
                                       setFormData((prevData) => ({
                                          ...prevData,
                                          ftpUserName: '',
                                          ftpPassword: '',
                                       }));
                                    }
                                 }}
                              />
                              <p>{t('common.need-username-and-password-to-access-ftp-location')}</p>
                           </div>
                           {inputChecked && (
                              <div className='grid grid-cols-2 gap-x-5'>
                                 <div className='col-span-1'>
                                    <CustomInput
                                       required={false}
                                       title={t('common.user-name')}
                                       inputType='text'
                                       name='ftpUserName'
                                       handleInputChange={handleInputChange}
                                       value={formData.ftpUserName}
                                    />
                                 </div>
                                 <div className='col-span-1'>
                                    <CustomInput
                                       required={false}
                                       title={t('common.password')}
                                       inputType='text'
                                       name='ftpPassword'
                                       handleInputChange={handleInputChange}
                                       value={formData.ftpPassword}
                                    />
                                 </div>
                              </div>
                           )}
                        </div>
                     )}
                  </div>
                  <div className='grid grid-cols-10 pt-5'>
                     <p className='col-span-10 md:col-span-3 flex items-start pt-3 text-sm'>{t('common.file-password')}</p>
                     <div className='col-span-10 md:col-span-7'>
                        <input
                           type='text'
                           className='form-control'
                           name='password'
                           value={formData.password}
                           onChange={handleInputChange}
                        />
                        <div className='bg-[#FEFCE8] p-3 text-[#A16207] mt-3'>
                           <p className='font-semibold text-sm'>{t('tool-tip.password.should-include')}</p>
                           <ul className='custom-dot-list'>
                              <li>{t('tool-tip.password.at-least-character')}</li>
                              <li>{t('tool-tip.password.at-least-letter')}</li>
                              <li>{t('tool-tip.password.none-english')}</li>
                           </ul>
                        </div>
                     </div>
                  </div>
                  <div className='grid grid-cols-10 pt-5'>
                     <p className='col-span-10 md:col-span-3 flex items-center text-sm'>{t('common.confirm-password')}</p>
                     <div className='col-span-10 md:col-span-7'>
                        <input type='text' className='form-control' />
                        <div className='col-span-10 pt-3'>
                           <ProgressPassword value={formData.password} />
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </DialogContent>

         <DialogActions>
            <div className='flex gap-2 px-4 py-4'>
               <ControlpanelSecondaryButton onClick={onClose}>
                  <CloseIcon />
                  <p>{t('button.cancel')}</p>
               </ControlpanelSecondaryButton>
               <ControlpanelPrimaryButton>
                  <PlayArrowIcon />
                  <p>{t('button.execute')}</p>
               </ControlpanelPrimaryButton>
            </div>
         </DialogActions>
      </CustomDialog>
   );
};

export default ExportDataDialog;
